import qs from 'qs';
import { parseBiasingStrategy } from '@/search-for-retail/biasing-strategy/biasing-strategy.parsers';
import { formatBiasingStrategy } from '@/search-for-retail/biasing-strategy/biasing-strategy.formatters';
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the biasing profile resource under an area. */
const BIASING_PROFILE_PATH = 'biasingProfile';
// ---------------------------------------------------------------------------
// Endpoints
// ---------------------------------------------------------------------------
/**
 * RTK Query endpoints for biasing strategy (biasing profile) data.
 *
 * Biasing strategy endpoints sit under `/{areaId}/biasingProfile`.
 * Auth and tenant headers are handled by the base `ccApi`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const biasingStrategyApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Biasing strategy queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the list of biasing strategies for an area.
         *
         * @endpoint GET /{areaId}/biasingProfile
         */
        getBiasingStrategyList: builder.query({
            query: ({ areaId }) => `/${areaId}/${BIASING_PROFILE_PATH}`,
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.map(parseBiasingStrategy),
            providesTags: [CC_API_TAGS.BiasingStrategyList],
        }),
        /**
         * Fetches a single biasing strategy by ID.
         *
         * @endpoint GET /{areaId}/biasingProfile/{id}
         */
        getBiasingStrategyById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${BIASING_PROFILE_PATH}/${id}`,
            transformResponse: (response) => parseBiasingStrategy(response),
            providesTags: (_result, _error, { id }) => [
                { type: CC_API_TAGS.BiasingStrategy, id },
            ],
        }),
        /**
         * Fetches usage information for a biasing strategy.
         *
         * No `providesTags` — usage data is fetched on-demand via the lazy hook
         * and does not need automatic cache invalidation after mutations.
         *
         * @endpoint GET /{areaId}/biasingProfile/{id}/usage
         */
        getBiasingStrategyUsageById: builder.query({
            query: ({ areaId, id }) => `/${areaId}/${BIASING_PROFILE_PATH}/${id}/usage`,
        }),
        /**
         * Looks up a biasing profile ID by profile name.
         *
         * @endpoint GET /{areaId}/biasingProfile/name/{name}
         */
        getBiasingProfileIdByName: builder.query({
            query: ({ areaId, name }) => `/${areaId}/${BIASING_PROFILE_PATH}/name/${name}`,
            transformResponse: (response) => response === null || response === void 0 ? void 0 : response.id,
        }),
        // -----------------------------------------------------------------------
        // Biasing strategy mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new biasing strategy in an area.
         *
         * @endpoint POST /{areaId}/biasingProfile
         */
        createBiasingStrategy: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${BIASING_PROFILE_PATH}`,
                method: 'POST',
                body: formatBiasingStrategy(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseBiasingStrategy(response),
            invalidatesTags: [CC_API_TAGS.BiasingStrategyList],
        }),
        /**
         * Updates an existing biasing strategy.
         *
         * @endpoint PUT /{areaId}/biasingProfile/{id}
         */
        updateBiasingStrategy: builder.mutation({
            query: ({ areaId, data }) => ({
                url: `/${areaId}/${BIASING_PROFILE_PATH}/${data.id}`,
                method: 'PUT',
                body: formatBiasingStrategy(Object.assign({ areaId }, data)),
            }),
            transformResponse: (response) => parseBiasingStrategy(response),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.BiasingStrategyList,
                { type: CC_API_TAGS.BiasingStrategy, id: data.id },
            ],
        }),
        /**
         * Deletes a single biasing strategy.
         *
         * @endpoint DELETE /{areaId}/biasingProfile/{id}
         */
        deleteBiasingStrategy: builder.mutation({
            query: ({ areaId, id }) => ({
                url: `/${areaId}/${BIASING_PROFILE_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.BiasingStrategyList,
                { type: CC_API_TAGS.BiasingStrategy, id },
            ],
        }),
        /**
         * Bulk-deletes biasing strategies.
         *
         * @endpoint DELETE /{areaId}/biasingProfile/bulk/delete?ids=...
         */
        bulkDeleteBiasingStrategy: builder.mutation({
            query: ({ areaId, idList }) => {
                const query = qs.stringify({ ids: idList }, { indices: false });
                return {
                    url: `/${areaId}/${BIASING_PROFILE_PATH}/bulk/delete?${query}`,
                    method: 'DELETE',
                };
            },
            invalidatesTags: [CC_API_TAGS.BiasingStrategyList],
        }),
        /**
         * Promotes a biasing strategy to another area.
         *
         * @endpoint POST /{areaId}/biasingProfile/{id}/promote/{targetAreaId}
         */
        promoteBiasingStrategyToArea: builder.mutation({
            query: ({ areaId, targetAreaId, id }) => ({
                url: `/${areaId}/${BIASING_PROFILE_PATH}/${id}/promote/${targetAreaId}`,
                method: 'POST',
            }),
            invalidatesTags: [CC_API_TAGS.BiasingStrategyList],
        }),
        /**
         * Bulk-promotes biasing strategies to another area.
         *
         * @endpoint POST /{areaId}/biasingProfile/bulk/promote/{targetAreaId}?ids=...
         */
        bulkPromoteBiasingStrategyToArea: builder.mutation({
            query: ({ areaId, targetAreaId, idList }) => {
                const query = qs.stringify({ ids: idList }, { indices: false });
                return {
                    url: `/${areaId}/${BIASING_PROFILE_PATH}/bulk/promote/${targetAreaId}?${query}`,
                    method: 'POST',
                };
            },
            invalidatesTags: [CC_API_TAGS.BiasingStrategyList],
        }),
    }),
});
export const { useGetBiasingStrategyListQuery, useLazyGetBiasingStrategyListQuery, useGetBiasingStrategyByIdQuery, useGetBiasingStrategyUsageByIdQuery, useLazyGetBiasingProfileIdByNameQuery, useLazyGetBiasingStrategyUsageByIdQuery, useCreateBiasingStrategyMutation, useUpdateBiasingStrategyMutation, useDeleteBiasingStrategyMutation, useBulkDeleteBiasingStrategyMutation, usePromoteBiasingStrategyToAreaMutation, useBulkPromoteBiasingStrategyToAreaMutation, } = biasingStrategyApi;
